import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Quote } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { IMG, BUSINESS } from "../lib/constants";
import TrustStrip from "../components/TrustStrip";

const Tattoos = () => {
    const { t } = useLanguage();
    const p = t.tattoosPage;
    const mailtoBody = encodeURIComponent(
        `Hi Elisha,\n\nI'd love to inquire about a fine line tattoo.\n\nIdea / reference:\nSize:\nApprox. budget:\nPreferred dates:\n\nName:\nPhone:\nInstagram:\n\nThanks!`
    );

    return (
        <>
            {/* Hero - About page style with video on right */}
            <section
                data-testid="tattoos-hero"
                className="bg-[#3D3D24] text-[#FFFFF3] overflow-hidden"
            >
                <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2 gap-0">
                    <div className="order-2 lg:order-1 px-6 md:px-12 lg:px-16 py-16 md:py-24 lg:py-28 flex flex-col justify-center">
                        <div className="font-label text-[11px] text-[#D4A95A] mb-5">{p.heroOverline}</div>
                        <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.98] mb-6">
                            {p.heroTitle}
                        </h1>
                        <p className="text-base md:text-lg opacity-85 max-w-2xl leading-relaxed mb-8">
                            {p.heroSub}
                        </p>
                        <a
                            data-testid="tattoos-hero-email"
                            href={`mailto:elishaleetattoo@gmail.com?subject=Tattoo%20Inquiry&body=${mailtoBody}`}
                            className="inline-flex items-center gap-2 bg-[#D4A95A] text-[#3D3D24] px-8 py-4 font-label text-xs hover:bg-[#FFFFF3] transition-colors w-fit"
                        >
                            Contact Elisha
                            <ArrowRight size={14} />
                        </a>
                    </div>
                    <div className="order-1 lg:order-2 relative">
                        <video
                            src={IMG.videoFineLineHero}
                            autoPlay
                            loop
                            muted
                            playsInline
                            className="w-full h-full object-cover object-center min-h-[420px] lg:min-h-[560px]"
                        />
                    </div>
                </div>
            </section>

            <TrustStrip variant="dark" />

            {/* Artist intro */}
            <section data-testid="tattoos-artist" className="bg-white py-24 md:py-32">
                <div className="max-w-[1200px] mx-auto px-6 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
                    <div className="relative aspect-[4/5] overflow-hidden order-2 lg:order-1 bg-white">
                        <img
                            src={IMG.elishaLee}
                            alt="Elisha Lee - Fine Line Tattoo Artist Las Vegas at Beauty Ink LV"
                            className="w-full h-full object-cover object-top bink-img-hover"
                        />
                        <div className="absolute bottom-6 left-6 bg-[#D4A95A] text-[#3D3D24] px-5 py-3">
                            <div className="font-label text-[10px] tracking-[0.2em] opacity-80">{p.artistOverline}</div>
                            <div className="font-serif text-2xl">{p.artistName}</div>
                        </div>
                    </div>

                    <div className="order-1 lg:order-2">
                        <div className="font-label text-[11px] text-[#D4A95A] mb-5">{p.artistOverline}</div>
                        <h2 className="font-serif text-4xl md:text-5xl text-[#3D3D24] leading-[1.05] mb-4">
                            {p.artistName}
                        </h2>
                        <div className="font-label text-xs text-[#3D3D24]/70 mb-6 tracking-[0.18em]">
                            {p.artistTitle}
                        </div>
                        <div className="border-l-2 border-[#D4A95A] pl-5 py-1 mb-8">
                            <p className="text-[#3D3D24]/85 leading-relaxed">{p.artistSpecialty}</p>
                        </div>

                        <div className="bg-[#F5F2E4] p-8 mb-8 relative">
                            <Quote size={32} className="text-[#D4A95A] mb-3" />
                            <p className="font-serif text-lg md:text-xl text-[#3D3D24] leading-snug italic mb-4">
                                {p.artistQuote}
                            </p>
                            <div className="font-label text-[10px] text-[#3D3D24]/70 tracking-[0.18em]">
                                {p.artistQuoteBy}
                            </div>
                        </div>

                        <a
                            data-testid="tattoos-book-elisha"
                            href={`mailto:${BUSINESS.email}?subject=${encodeURIComponent("Inquiry — Fine Line Tattoo with Elisha")}&body=${mailtoBody}`}
                            className="inline-flex items-center gap-2 bg-[#3D3D24] text-[#FFFFF3] px-8 py-4 font-label text-xs hover:bg-[#D4A95A] hover:text-[#3D3D24] transition-colors"
                        >
                            {p.bookWithElisha}
                            <ArrowRight size={14} />
                        </a>
                    </div>
                </div>
            </section>

            {/* Portfolio */}
            <section data-testid="tattoos-gallery" className="bg-[#3D3D24] text-[#FFFFF3] py-24 md:py-32">
                <div className="max-w-[1400px] mx-auto px-6">
                    <div className="mb-12">
                        <div className="font-label text-[11px] text-[#D4A95A] mb-5">{p.galleryOverline}</div>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02]">
                            {p.galleryTitle}
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4 md:gap-6">
                        {/* Video 1 */}
                        <div data-testid="tattoo-video-1" className="overflow-hidden aspect-[4/5]">
                            <video
                                src={IMG.videoFineLineTattoo1}
                                autoPlay
                                loop
                                muted
                                playsInline
                                className="w-full h-full object-cover"
                            />
                        </div>
                        {/* Video 2 */}
                        <div data-testid="tattoo-video-2" className="overflow-hidden aspect-[4/5]">
                            <video
                                src={IMG.videoFineLineTattoo2}
                                autoPlay
                                loop
                                muted
                                playsInline
                                className="w-full h-full object-cover"
                            />
                        </div>
                        {/* Video 3 - Fine Line New */}
                        <div data-testid="tattoo-video-3" className="overflow-hidden aspect-[4/5]">
                            <video
                                src={IMG.videoFineLineNew}
                                autoPlay
                                loop
                                muted
                                playsInline
                                className="w-full h-full object-cover"
                            />
                        </div>
                        {/* Video 4 - Fine Line */}
                        <div data-testid="tattoo-video-4" className="overflow-hidden aspect-[4/5]">
                            <video
                                src={IMG.videoFineLine3}
                                autoPlay
                                loop
                                muted
                                playsInline
                                className="w-full h-full object-cover"
                            />
                        </div>
                        {/* Video 5 - Fine Line 2 */}
                        <div data-testid="tattoo-video-5" className="overflow-hidden aspect-[4/5]">
                            <video
                                src={IMG.videoFineLine4}
                                autoPlay
                                loop
                                muted
                                playsInline
                                className="w-full h-full object-cover"
                            />
                        </div>
                        {/* Video 6 - Fine Line 1 More */}
                        <div data-testid="tattoo-video-6" className="overflow-hidden aspect-[4/5]">
                            <video
                                src={IMG.videoFineLine5}
                                autoPlay
                                loop
                                muted
                                playsInline
                                className="w-full h-full object-cover"
                            />
                        </div>
                        {[
                            { src: IMG.tattooButterfly, alt: "Fine line butterfly tattoo Las Vegas" },
                            { src: IMG.tattooOrnamental, alt: "Ornamental fine line tattoo Las Vegas" },
                        ].map((item, i) => (
                            <div
                                key={i}
                                data-testid={`tattoo-gallery-${i}`}
                                className="overflow-hidden aspect-[4/5]"
                            >
                                <img src={item.src} alt={item.alt} className="w-full h-full object-cover bink-img-hover" />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Space for rent (link to bed rentals) */}
            <section data-testid="tattoos-space" className="bg-[#FFFFF3] py-24 md:py-32">
                <div className="max-w-[1200px] mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
                    <div className="overflow-hidden aspect-[4/5] order-2 lg:order-1">
                        <img src={IMG.studio1} alt="Beauty Ink LV studio space" className="w-full h-full object-cover bink-img-hover" />
                    </div>
                    <div className="order-1 lg:order-2">
                        <div className="font-label text-[11px] text-[#D4A95A] mb-5">For Tattoo Artists</div>
                        <h2 className="font-serif text-4xl md:text-5xl text-[#3D3D24] leading-[1.05] mb-6">
                            {p.spaceTitle}
                        </h2>
                        <p className="text-[#3D3D24]/85 text-base md:text-lg leading-relaxed mb-8 max-w-lg">
                            {p.spaceBody}
                        </p>
                        <Link
                            data-testid="tattoos-space-cta"
                            to="/bed-rentals"
                            className="inline-flex items-center gap-2 border border-[#3D3D24] text-[#3D3D24] px-8 py-4 font-label text-xs hover:bg-[#3D3D24] hover:text-[#FFFFF3] transition-colors"
                        >
                            {p.spaceCta}
                            <ArrowRight size={14} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Location */}
            <section data-testid="tattoos-location" className="bg-[#F5F2E4] py-24 md:py-32">
                <div className="max-w-[1100px] mx-auto px-6 text-center">
                    <div className="font-label text-[11px] text-[#D4A95A] mb-5 flex items-center justify-center gap-2">
                        <MapPin size={14} /> Las Vegas, NV
                    </div>
                    <h2 className="font-serif text-3xl md:text-5xl text-[#3D3D24] leading-[1.05] mb-6 max-w-3xl mx-auto">
                        {p.locationTitle}
                    </h2>
                    <p className="text-[#3D3D24]/80 text-base leading-relaxed max-w-2xl mx-auto mb-3">
                        {p.locationBody}
                    </p>
                    <a
                        data-testid="tattoos-address"
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(BUSINESS.address)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-serif text-xl text-[#3D3D24] hover:text-[#D4A95A] transition-colors inline-block"
                    >
                        {BUSINESS.address}
                    </a>
                </div>
            </section>
        </>
    );
};

export default Tattoos;
